import {
  CallQueueRow,
  DashboardChartCard,
  DashboardMetricCard,
  DashboardMetricGrid,
  DashboardSectionAction,
  DashboardSectionTitle,
  CallsListSkeleton,
  Button,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  useCallRecords,
  useLiveCallRecordsStream,
  navigateWithThreadPreservation,
  cn,
} from '@yeshuman/sdk'
import type { ChartConfig } from '@yeshuman/sdk'
import { useMemo, useState } from 'react'
import { ChevronDown, ClipboardList, CalendarCheck, PhoneCall, CheckCircle2 } from 'lucide-react'
import { Cell, Pie, PieChart } from 'recharts'
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { CallAgentPerformancePanel } from './call-agent-performance-panel'
import { CallAttentionModule } from './call-attention-module'
import { CallLivePulseStrip } from './call-live-pulse-strip'
import { CallOutcomeRow } from './call-outcome-row'
import { useCallOutcomes } from '../hooks/use-call-outcomes'
import {
  buildCallOperatorCallMetrics,
  buildCallOperatorOutcomeMetrics,
  isEvaluationBacklog,
  latestOutcomePerCall,
  selectAttentionCalls,
} from '../lib/call-operator-metrics'

const DASHBOARD_LIMIT = 200

const countChartConfig = {
  count: {
    label: 'Count',
    color: 'var(--chart-1)',
  },
} satisfies ChartConfig

const statusDonutConfig = {
  value: { label: 'Calls' },
} satisfies ChartConfig

const STATUS_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
]

function truncateLabel(s: string, max = 18) {
  if (s.length <= max) return s
  return `${s.slice(0, max - 1)}…`
}

export function CallOperatorDashboardSections() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [snapshotOpen, setSnapshotOpen] = useState(false)

  const recordsQuery = useCallRecords(DASHBOARD_LIMIT)
  const outcomesQuery = useCallOutcomes(DASHBOARD_LIMIT, {
    refetchInterval: 45_000,
  })

  const sse = useLiveCallRecordsStream({
    enabled: true,
    invalidateOnTerminal: 'call-outcomes',
  })

  const records = recordsQuery.data ?? []
  const outcomes = outcomesQuery.data ?? []

  const callMetrics = useMemo(
    () => buildCallOperatorCallMetrics(records),
    [records]
  )
  const outcomeMetrics = useMemo(
    () => buildCallOperatorOutcomeMetrics(records, outcomes),
    [records, outcomes]
  )

  const attentionCalls = useMemo(
    () => selectAttentionCalls(records, 4),
    [records]
  )

  const evalBacklogRows = useMemo(
    () =>
      [...records]
        .filter(isEvaluationBacklog)
        .sort((a, b) => b.updated_at.localeCompare(a.updated_at))
        .slice(0, 3),
    [records]
  )

  const recentOutcomes = useMemo(() => {
    const latest = latestOutcomePerCall(outcomes)
    return [...latest.values()]
      .sort((a, b) => b.updated_at.localeCompare(a.updated_at))
      .slice(0, 3)
  }, [outcomes])

  const recordsById = useMemo(() => {
    const map = new Map(records.map((r) => [r.id, r]))
    return map
  }, [records])

  const statusDonutData = useMemo(
    () =>
      callMetrics.callStatusSeries.slice(0, 6).map((s, i) => ({
        name: truncateLabel(s.name),
        value: s.count,
        fill: STATUS_COLORS[i % STATUS_COLORS.length],
      })),
    [callMetrics.callStatusSeries]
  )

  const durationHistData = useMemo(
    () =>
      callMetrics.durationHistogram.map((s) => ({
        name: s.name,
        count: s.count,
      })),
    [callMetrics.durationHistogram]
  )

  const evalStatusChips = callMetrics.evaluationStatusSeries

  const loading = recordsQuery.isLoading || outcomesQuery.isLoading
  const error = recordsQuery.error || outcomesQuery.error
  const emptyWindow = !loading && !error && records.length === 0

  return (
    <div className="space-y-6">
      {error ? (
        <p className="text-sm text-destructive">{(error as Error).message}</p>
      ) : null}

      <CallLivePulseStrip
        activeCount={callMetrics.activeCount}
        sseStatus={sse.status}
        lastEventAt={sse.lastEventAt}
        loading={loading}
      />

      {emptyWindow ? (
        <div className="rounded-lg border border-dashed bg-muted/20 px-4 py-8 space-y-4 text-center">
          <p className="text-sm font-medium text-foreground">
            No calls in the current window yet
          </p>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Typical shift flow: open the live board, work the evaluations queue, then
            record booking outcomes.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <DashboardSectionAction
              onClick={() =>
                navigateWithThreadPreservation(
                  navigate,
                  '/calls/records',
                  searchParams
                )
              }
            >
              Open live board
            </DashboardSectionAction>
          </div>
        </div>
      ) : (
        <CallAttentionModule
          attentionCalls={attentionCalls}
          evaluationBacklogCount={callMetrics.evaluationBacklogCount}
          loading={loading}
        />
      )}

      <section className="space-y-4" aria-labelledby="agent-performance-heading">
        <DashboardSectionTitle
          id="agent-performance-heading"
          title="Agent performance"
        />
        <p className="text-xs text-muted-foreground -mt-2">
          Evaluation throughput, booking conversion, and call endings for the latest{' '}
          {DASHBOARD_LIMIT} rows.
        </p>
        <CallAgentPerformancePanel
          callMetrics={callMetrics}
          outcomeMetrics={outcomeMetrics}
          loading={loading}
        />
      </section>

      <section className="space-y-4" aria-labelledby="shift-kpis-heading">
        <DashboardSectionTitle id="shift-kpis-heading" title="Today's shift" />
        <DashboardMetricGrid maxColumns={4} showIcons>
          <DashboardMetricCard
            title="Active calls"
            value={loading ? '…' : callMetrics.activeCount}
            icon={PhoneCall}
            loading={loading}
          />
          <DashboardMetricCard
            title="Completed today"
            value={loading ? '…' : callMetrics.completedToday}
            icon={CheckCircle2}
            loading={loading}
          />
          <button
            type="button"
            className="h-full rounded-lg text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() =>
              navigateWithThreadPreservation(
                navigate,
                '/calls/outcomes?window=upcoming',
                searchParams
              )
            }
          >
            <DashboardMetricCard
              title="Bookings made"
              value={loading ? '…' : outcomeMetrics.bookingsMade}
              icon={CalendarCheck}
              description="Open upcoming appointments"
              loading={loading}
            />
          </button>
          <DashboardMetricCard
            title="Evaluations backlog"
            value={loading ? '…' : callMetrics.evaluationBacklogCount}
            icon={ClipboardList}
            description="Needs attention"
            loading={loading}
          />
        </DashboardMetricGrid>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="ghostMuted"
            size="sm"
            onClick={() =>
              navigateWithThreadPreservation(
                navigate,
                '/calls/records',
                searchParams
              )
            }
          >
            Go to live board
          </Button>
          <Button
            type="button"
            variant="ghostMuted"
            size="sm"
            onClick={() =>
              navigateWithThreadPreservation(
                navigate,
                '/calls/evaluations',
                searchParams
              )
            }
          >
            Go to evaluations
          </Button>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 @3col:grid-cols-2">
        <section className="space-y-4" aria-labelledby="eval-preview-heading">
          <DashboardSectionTitle
            id="eval-preview-heading"
            title="Evaluations backlog"
            action={
              <DashboardSectionAction
                onClick={() =>
                  navigateWithThreadPreservation(
                    navigate,
                    '/calls/evaluations',
                    searchParams
                  )
                }
              >
                View queue
              </DashboardSectionAction>
            }
          />
          {loading && evalBacklogRows.length === 0 ? (
            <CallsListSkeleton count={3} ariaLabel="Loading evaluation backlog" />
          ) : evalBacklogRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No evaluations waiting — queue is clear.
            </p>
          ) : (
            <div className="space-y-2">
              {evalBacklogRows.map((row) => (
                <CallQueueRow
                  key={row.id}
                  variant="evaluation"
                  row={row}
                  showEvalSummary
                />
              ))}
            </div>
          )}
        </section>

        <section className="space-y-4" aria-labelledby="outcomes-preview-heading">
          <DashboardSectionTitle
            id="outcomes-preview-heading"
            title="Recent outcomes"
            action={
              <DashboardSectionAction
                onClick={() =>
                  navigateWithThreadPreservation(
                    navigate,
                    '/calls/outcomes?window=upcoming',
                    searchParams
                  )
                }
              >
                View all
              </DashboardSectionAction>
            }
          />
          {loading && recentOutcomes.length === 0 ? (
            <CallsListSkeleton count={3} ariaLabel="Loading recent outcomes" />
          ) : recentOutcomes.length === 0 ? (
            <p className="text-sm text-muted-foreground">No outcomes yet.</p>
          ) : (
            <div className="space-y-2">
              {recentOutcomes.map((o) => (
                <CallOutcomeRow
                  key={o.id}
                  outcome={o}
                  row={recordsById.get(o.call_record_id) ?? null}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      <Collapsible open={snapshotOpen} onOpenChange={setSnapshotOpen}>
        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="ghostMuted"
            className="w-full justify-between border border-dashed"
          >
            <span>Today&apos;s snapshot</span>
            <ChevronDown
              className={cn(
                'size-4 transition-transform',
                snapshotOpen && 'rotate-180'
              )}
              aria-hidden
            />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4 pt-4">
          <div className="grid grid-cols-1 gap-4 @3col:grid-cols-2">
            <DashboardChartCard
              title="Call status mix"
              description="Statuses in the current window"
              loading={loading}
            >
              {statusDonutData.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  No status data.
                </p>
              ) : (
                <ChartContainer
                  config={statusDonutConfig}
                  className="mx-auto aspect-square max-h-[220px] w-full"
                >
                  <PieChart>
                    <ChartTooltip content={<ChartTooltipContent nameKey="name" />} />
                    <Pie
                      data={statusDonutData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius="55%"
                      outerRadius="85%"
                      strokeWidth={2}
                    >
                      {statusDonutData.map((entry) => (
                        <Cell key={entry.name} fill={entry.fill} />
                      ))}
                    </Pie>
                  </PieChart>
                </ChartContainer>
              )}
            </DashboardChartCard>

            <DashboardChartCard
              title="Call duration"
              description="Completed calls (bucketed)"
              loading={loading}
            >
              <ChartContainer
                config={countChartConfig}
                className="min-h-[200px] w-full"
              >
                <BarChart
                  accessibilityLayer
                  data={durationHistData}
                  layout="vertical"
                  margin={{ left: 4, right: 12, top: 8, bottom: 8 }}
                >
                  <CartesianGrid horizontal={false} />
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    dataKey="name"
                    type="category"
                    width={72}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11 }}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="count" fill="var(--color-count)" radius={4} />
                </BarChart>
              </ChartContainer>
            </DashboardChartCard>
          </div>

          <div>
            <p className="text-sm font-medium text-foreground mb-2">
              Evaluation status
            </p>
            {evalStatusChips.length === 0 ? (
              <p className="text-sm text-muted-foreground">No evaluation data.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {evalStatusChips.map((s) => (
                  <span
                    key={s.name}
                    className="inline-flex items-center gap-1.5 rounded-full border bg-muted/40 px-2.5 py-1 text-xs"
                  >
                    <span className="font-medium text-foreground">
                      {truncateLabel(s.name, 24)}
                    </span>
                    <span className="tabular-nums text-muted-foreground">
                      {s.count}
                    </span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      <p className="text-xs text-muted-foreground">
        Metrics reflect the latest {DASHBOARD_LIMIT} call rows and outcomes, not full
        history. &quot;Completed today&quot; uses your local calendar day.
      </p>
    </div>
  )
}
